/**
 * Legacy constraint migration — Unity-era `.trace` scenes (#717).
 *
 * Three authored shapes from the ContentSDK era have no runtime here, and
 * carrying them as a shim forever is the thing DESIGN_PRINCIPLES §2 tells us
 * not to do. So they are compiled, once, at the load boundary, into surfaces
 * that already ship:
 *
 * | Legacy                      | Compiles to                                    |
 * |-----------------------------|------------------------------------------------|
 * | `Properties.props` entry    | a `properties` declaration + a bound transform  |
 * | `Follow { target }`         | a bound (or already-satisfied) base transform   |
 * | `FollowActive { targets }`  | one state per page + `Pages` state assignments  |
 *
 * ## Why a compile and not a constraint kernel
 *
 * Legacy `Follow` is an exact world-position copy, evaluated every frame in
 * Unity. Every known legacy scene is static apart from one slider and the
 * cyclers: nothing animates, so each constraint is worth either a constant or
 * a linear function of that one slider. A per-frame kernel would be runtime
 * machinery (§5, rung 3) re-implemented by every client to reproduce values
 * we can compute here, once, at load.
 *
 * The compile is numeric, not symbolic: the hierarchy already exists as
 * `TransformNode`s, so the required pose is *measured* by probing the driver
 * through its range and reading world matrices. That also makes the
 * assumption falsifiable — a target whose response to the driver isn't linear
 * fails the midpoint check and is reported instead of silently mis-compiled.
 *
 * ## Why `FollowActive` nests the follower
 *
 * "Sit on whichever of these markers is active" is a function of one page
 * axis, so the follower's pose is expressed as **one number per moving axis,
 * declared on the container that owns the axis** — `desksHeight` on the
 * stage deck, `peopleHeight` on the desk deck — with each page overriding
 * that number through a self-targeted value state. The follower becomes a
 * `shared` child of the container (PAGES_SPEC §3) and binds the number in
 * its base transform.
 *
 * Nesting is what makes chains compose. People follow the desk marker, desks
 * follow the stage marker; with People beneath Desks beneath Stages, the
 * stage lift reaches People by parenting and every follower carries exactly
 * one binding. The earlier form — absolute transform states on a sibling —
 * baked the rest of the scene's authored pose into each state, so a stage
 * with a riser left the people behind (#745).
 *
 * ## Why `Follow` is never constructed
 *
 * A legacy `Follow` payload is shape-compatible with a motion behavior, and
 * for one release it WAS routed into one: the object chased its target at
 * 1.5 m/s and, because export captures the live transform, play-then-save
 * baked the drift. `Follow` is now a retired `@type` — the motion behavior is
 * `Tether` — so this module is the only thing that ever reads it.
 */

import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Logger } from '../utils/Logger';
import type { BehaviorData } from '../scenedata/behaviordata';
import type { PropertiesData, PropertyDeclaration, TransformComponent } from '../scenedata/behaviors/propertiesdata';
import { numericTuple } from '../scenedata/behaviors/propertiesdata';
import type { PagesData, PageData } from '../scenedata/behaviors/pagesdata';
import type { FollowActiveData, FollowData, PropertiesData as LegacyPropertiesData } from '../scenedata/legacy/stubs';

/**
 * The slice of a loaded node this migration needs: its name, its live
 * transform (already parented, so world matrices are readable), and its
 * authored behavior list, which the migration rewrites in place.
 */
export interface LegacyConstraintNode {
    name: string;
    transform: TransformNode;
    behaviors?: BehaviorData[];
    /** Authored active flag — decides which page's pose is the base. */
    active?: boolean;
}

/** Which transform component a legacy `Transform.*` property path addresses. */
interface DrivenComponent {
    kind: 'position' | 'scale';
    axis: 0 | 1 | 2;
}

/** A declared property wired to one component of one node's transform. */
interface Driver extends DrivenComponent {
    property: string;
    /** The node whose transform component the property drives. */
    node: LegacyConstraintNode;
    /** The node carrying the declaration — bindings only resolve below it. */
    owner: LegacyConstraintNode;
}

/** Positions equal to within this (metres) are the same position. */
const EPSILON = 1e-5;

/** Probe points for the linearity check — endpoints plus the midpoint. */
const PROBE_LOW = 0;
const PROBE_HIGH = 1;

// ============================================================================
// Entry points
// ============================================================================

/** Whether any node carries a legacy constraint or a legacy `props` list. */
export function hasLegacyConstraints(nodes: readonly LegacyConstraintNode[]): boolean {
    return nodes.some(n => (n.behaviors ?? []).some(isLegacy));
}

/**
 * Compile every legacy constraint in the scene, in place.
 *
 * Runs before SceneObjects are constructed, so no behavior ever sees the
 * legacy data. Idempotent: a migrated scene carries none of the legacy
 * `@type`s, so a second pass is a no-op.
 */
export function migrateLegacyConstraints(nodes: readonly LegacyConstraintNode[]): void {
    if (!hasLegacyConstraints(nodes)) return;

    const index = new SceneIndex(nodes);
    const drivers = migrateProps(nodes, index);
    migrateConstraints(nodes, index, drivers);
}

// ============================================================================
// Scene index — legacy paths, relative paths, world/local math
// ============================================================================

class SceneIndex {
    private readonly _byPath = new Map<string, LegacyConstraintNode>();
    private readonly _byName = new Map<string, LegacyConstraintNode[]>();
    private readonly _byTransform = new Map<TransformNode, LegacyConstraintNode>();

    constructor(private readonly _nodes: readonly LegacyConstraintNode[]) {
        for (const node of _nodes) {
            this._byPath.set(this.pathOf(node), node);
            const bucket = this._byName.get(node.name);
            if (bucket) bucket.push(node); else this._byName.set(node.name, [node]);
            this._byTransform.set(node.transform, node);
        }
    }

    /** Full path from the file's own root, the form legacy targets are authored in. */
    pathOf(node: LegacyConstraintNode): string {
        const parts: string[] = [];
        let current: TransformNode | null = node.transform;
        while (current) {
            parts.unshift(current.name);
            current = current.parent as TransformNode | null;
        }
        return parts.join('/');
    }

    /**
     * Resolve a legacy `TransformPath`. Exact full path first; a bare-name
     * match is the documented legacy fallback, and only accepted when it is
     * unambiguous — legacy scenes reuse names across branches.
     */
    resolve(path: string | undefined): LegacyConstraintNode | undefined {
        if (!path) return undefined;
        const exact = this._byPath.get(path);
        if (exact) return exact;
        const leaf = path.split('/').filter(Boolean).pop();
        const candidates = leaf ? this._byName.get(leaf) : undefined;
        return candidates?.length === 1 ? candidates[0] : undefined;
    }

    nodeFor(transform: TransformNode | null): LegacyConstraintNode | undefined {
        return transform ? this._byTransform.get(transform) : undefined;
    }

    /** The nearest ancestor (self included) satisfying `predicate`. */
    ancestorWhere(
        node: LegacyConstraintNode,
        predicate: (n: LegacyConstraintNode) => boolean,
    ): LegacyConstraintNode | undefined {
        let current: LegacyConstraintNode | undefined = node;
        while (current) {
            if (predicate(current)) return current;
            current = this.nodeFor(current.transform.parent as TransformNode | null);
        }
        return undefined;
    }

    /**
     * The local position `follower` must hold for its world position to equal
     * `target`'s — the whole content of a legacy Follow.
     */
    requiredLocalPosition(target: LegacyConstraintNode, follower: LegacyConstraintNode): Vector3 {
        return this.positionIn(target, this.nodeFor(follower.transform.parent as TransformNode | null));
    }

    /** `target`'s world position expressed in `frame`'s local space (world when no frame). */
    positionIn(target: LegacyConstraintNode, frame: LegacyConstraintNode | undefined): Vector3 {
        const world = this.worldPositionOf(target);
        if (!frame) return world;
        this.refresh(frame.transform);
        return Vector3.TransformCoordinates(world, Matrix.Invert(frame.transform.getWorldMatrix()));
    }

    worldPositionOf(node: LegacyConstraintNode): Vector3 {
        this.refresh(node.transform);
        return node.transform.getAbsolutePosition().clone();
    }

    /**
     * Recompute world matrices root-down. `computeWorldMatrix(true)` only
     * forces the node itself, so a driver written on an ancestor would
     * otherwise be read through that ancestor's stale matrix.
     */
    refresh(node: TransformNode): void {
        const chain: TransformNode[] = [];
        let current: TransformNode | null = node;
        while (current) {
            chain.unshift(current);
            current = current.parent as TransformNode | null;
        }
        for (const link of chain) link.computeWorldMatrix(true);
    }
}

// ============================================================================
// Pass 1 — Properties.props
// ============================================================================

/**
 * Convert each legacy `props` entry into a declaration on the owner plus a
 * bound transform component on the controlled object.
 *
 * `Cycler.active` entries are dropped: the Cycler → Pages migration already
 * gives AutoUI `next`/`prev` actions for exactly those objects, which is what
 * the legacy control was.
 */
function migrateProps(
    nodes: readonly LegacyConstraintNode[],
    index: SceneIndex,
): Driver[] {
    const drivers: Driver[] = [];

    for (const node of nodes) {
        const legacy = (node.behaviors ?? []).find(
            b => b['@type'] === 'Properties' && Array.isArray((b as LegacyPropertiesData).props),
        ) as (LegacyPropertiesData & PropertiesData) | undefined;
        if (!legacy) continue;

        const declarations: PropertyDeclaration[] = [...(legacy.properties ?? [])];
        const taken = new Set(declarations.map(d => d.name));

        for (const entry of legacy.props ?? []) {
            const component = parseTransformProperty(entry.property);
            if (!component) {
                if (!entry.property?.startsWith('Cycler.')) {
                    Logger.warn(`[LegacyConstraints] "${entry.label ?? entry.property}" addresses ${entry.property} — no equivalent control, dropped`);
                }
                continue;
            }
            const target = index.resolve(entry.obj);
            if (!target) {
                Logger.warn(`[LegacyConstraints] "${entry.label ?? entry.property}" targets ${entry.obj}, which is not in the scene — dropped`);
                continue;
            }

            const name = uniqueName(entry.label ?? entry.property ?? 'property', taken);
            taken.add(name);
            const value = readComponent(target.transform, component);
            declarations.push(numberDeclaration(name, entry.label, value, component));
            bindTransformComponent(target, component, `{${name}}`);
            const driver: Driver = { ...component, property: name, node: target, owner: node };
            warnIfUnreachable(target, driver, index);
            drivers.push(driver);
        }

        delete (legacy as Partial<LegacyPropertiesData>).props;
        legacy.properties = declarations;
    }

    return drivers;
}

/** `"Transform.localScale.y"` → `{ kind: 'scale', axis: 1 }`. */
function parseTransformProperty(property: string | undefined): DrivenComponent | undefined {
    const match = /^Transform\.local(Scale|Position)\.([xyz])$/.exec(property ?? '');
    if (!match) return undefined;
    return {
        kind: match[1] === 'Scale' ? 'scale' : 'position',
        axis: { x: 0, y: 1, z: 2 }[match[2] as 'x' | 'y' | 'z'] as 0 | 1 | 2,
    };
}

/**
 * A number declaration with a range legacy data never carried.
 *
 * AutoUI's slider falls back to 0..100, which for a scale factor is a control
 * the user cannot land on a useful value with. A scale gets 0..2 (the legacy
 * platform is authored between "flat" and "double"); a position gets ±2 m
 * around where the scene was left.
 */
function numberDeclaration(
    name: string,
    label: string | undefined,
    value: number,
    component: DrivenComponent,
): PropertyDeclaration {
    const scale = component.kind === 'scale';
    return {
        name,
        type: 'number',
        value,
        ...(label ? { label } : {}),
        min: scale ? 0 : value - 2,
        max: scale ? Math.max(2, value * 2) : value + 2,
        step: 0.01,
        inherits: true,
    };
}

/**
 * The state name of each page of a container, by page index — decided once
 * per container and reused by every follower of it, so two followers share
 * a page's state (a node holds one custom state at a time; two per page
 * would fight) while two same-named pages do NOT (legacy scenes reuse
 * names across siblings; a shared name would let the second page's pose
 * overwrite the first's).
 *
 * Named after the page, kept readable rather than camelCased — this is
 * what an editor shows on the state strip, and "Desk_VersaEdge" is the page
 * the author already knows. A leading `:` would make it a pseudo-state, so
 * it is stripped; a repeat gets a numbered suffix.
 */
const pageStateNames = new WeakMap<LegacyConstraintNode, string[]>();

function pageStateNamesOf(container: LegacyConstraintNode, pageNodes: readonly LegacyConstraintNode[]): string[] {
    const memo = pageStateNames.get(container);
    if (memo && memo.length === pageNodes.length) return memo;
    const taken = new Set<string>();
    const names = pageNodes.map(page => {
        const base = page.name.replace(/^:+/, '').trim() || 'page';
        let name = base;
        for (let n = 2; taken.has(name); n++) name = `${base} ${n}`;
        taken.add(name);
        return name;
    });
    pageStateNames.set(container, names);
    return names;
}

/** camelCase a legacy label into a property name, deduplicated. */
function uniqueName(label: string, taken: ReadonlySet<string>): string {
    const words = label.replace(/[^A-Za-z0-9]+/g, ' ').trim().split(/\s+/);
    const base = words
        .map((w, i) => (i === 0 ? w.toLowerCase() : w[0].toUpperCase() + w.slice(1).toLowerCase()))
        .join('') || 'property';
    if (!taken.has(base)) return base;
    let n = 2;
    while (taken.has(`${base}${n}`)) n++;
    return `${base}${n}`;
}

// ============================================================================
// Pass 2 — Follow / FollowActive
// ============================================================================

function migrateConstraints(
    nodes: readonly LegacyConstraintNode[],
    index: SceneIndex,
    drivers: readonly Driver[],
): void {
    for (const node of nodes) {
        const behaviors = node.behaviors;
        if (!behaviors) continue;

        for (const behavior of [...behaviors]) {
            if (behavior['@type'] === 'Follow') {
                compileFollow(node, behavior as FollowData, index, drivers);
                remove(behaviors, behavior);
            } else if (behavior['@type'] === 'FollowActive') {
                compileFollowActive(node, behavior as FollowActiveData, index, drivers);
                remove(behaviors, behavior);
            }
        }
    }
}

/**
 * A single exact-target constraint.
 *
 * Emits nothing when the authored pose already satisfies it — which is the
 * common case, because Unity wrote the constraint's own output into the
 * authored transform. Only a target that MOVES (because a driver moves it) is
 * worth authoring, and then as a bound component.
 */
function compileFollow(
    follower: LegacyConstraintNode,
    data: FollowData,
    index: SceneIndex,
    drivers: readonly Driver[],
): void {
    const target = index.resolve(data.target);
    if (!target) {
        Logger.warn(`[LegacyConstraints] Follow on "${follower.name}" targets ${data.target}, which is not in the scene — dropped`);
        return;
    }

    const solved = solveConstraint(target, follower, index, drivers);
    if (!solved) return;
    const constant = numericTuple(solved);
    if (constant && matchesAuthored(follower.transform.position, constant)) return;
    bindTransformPosition(follower, solved);
}

/** Property-name suffix per axis: `<follower> Height` reads as the value it is. */
const AXIS_LABEL = ['Offset X', 'Height', 'Offset Z'] as const;

/**
 * "Constrain to whichever of these is active."
 *
 * Which target is active is decided by a cycler, so the follower nests under
 * that cycler's node as a shared child and takes its pose from it: an axis
 * whose required position differs between pages becomes a number declared
 * on the container, overridden per page by a self-targeted value state and
 * bound in the follower's base transform; an axis that never moves is just
 * the follower's authored position. A follower whose every axis is constant
 * needs no property at all — nesting alone reproduces the constraint.
 */
function compileFollowActive(
    follower: LegacyConstraintNode,
    data: FollowActiveData,
    index: SceneIndex,
    drivers: readonly Driver[],
): void {
    const targets = (data.targets ?? [])
        .map(path => ({ path, node: index.resolve(path) }))
        .filter((t): t is { path: string; node: LegacyConstraintNode } => {
            if (!t.node) Logger.warn(`[LegacyConstraints] FollowActive on "${follower.name}" targets ${t.path}, which is not in the scene — dropped`);
            return !!t.node;
        });
    if (targets.length === 0) return;

    const container = drivingContainer(targets.map(t => t.node), index);
    if (!container) {
        Logger.warn(`[LegacyConstraints] FollowActive on "${follower.name}" has no cycler over its targets — dropped`);
        return;
    }
    if (contains(follower, container)) {
        Logger.warn(`[LegacyConstraints] FollowActive on "${follower.name}" targets its own subtree — dropped`);
        return;
    }

    // Pages are the container's children, minus anything an earlier compile
    // already parked there as a shared child.
    const pages = pagesOf(container);
    const shared = new Set((pages.shared ?? []).map(p => p.replace(/^\.\//, '')));
    const pageNodes = container.transform.getChildren<TransformNode>()
        .map(child => index.nodeFor(child))
        .filter((n): n is LegacyConstraintNode => !!n && !shared.has(n.name));

    // Where the follower must sit, per page, in the container's frame.
    const perPage = new Map<number, [number, number, number]>();
    for (const { node: target } of targets) {
        const pageIndex = pageNodes.findIndex(page => contains(page, target));
        if (pageIndex < 0) continue;
        const solved = solveConstraint(target, follower, index, drivers, container);
        if (!solved) continue;
        let pose = numericTuple(solved) as [number, number, number] | undefined;
        if (!pose) {
            // A page offset that tracks a control would need a per-page
            // expression; nothing authored does this, so pin it and say so.
            Logger.warn(`[LegacyConstraints] "${follower.name}" on page "${pageNodes[pageIndex].name}" tracks a control — pinned at the control's current value`);
            pose = index.positionIn(target, container).asArray();
        }
        perPage.set(pageIndex, pose);
    }
    if (perPage.size === 0) return;

    // The page active at authoring supplies the base pose — that is the pose
    // the authored scene shows, and the one every other page reverts to.
    const indices = [...perPage.keys()].sort((a, b) => a - b);
    const activeIndex = indices.find(i => pageNodes[i].active !== false);
    if (activeIndex === undefined) {
        // Every page authored inactive: no page's pose is "the one the scene
        // shows", so the follower may pop to the first page's pose on load.
        Logger.warn(`[LegacyConstraints] "${follower.name}": no page of "${container.name}" is active — basing its pose on "${pageNodes[indices[0]].name}"`);
    }
    const baseIndex = activeIndex ?? indices[0];
    const base = perPage.get(baseIndex)!;
    const varying = ([0, 1, 2] as const).filter(axis =>
        [...perPage.values()].some(pose => Math.abs(pose[axis] - base[axis]) > EPSILON));

    // Nest beneath the container at the base pose. World pose is preserved
    // by setParent; the authored scene already satisfied the constraint on
    // its active page, so this is normally a no-op write.
    index.refresh(follower.transform);
    follower.transform.setParent(container.transform);
    follower.transform.position.set(base[0], base[1], base[2]);
    pages.shared = [...(pages.shared ?? []), `./${follower.name}`];

    if (varying.length === 0) return;

    const containerProps = propertiesOf(container);
    const taken = new Set(containerProps.properties.map(d => d.name));
    const names = new Map<number, string>();
    for (const axis of varying) {
        const name = uniqueName(`${follower.name} ${AXIS_LABEL[axis]}`, taken);
        taken.add(name);
        names.set(axis, name);
        // Driven by page selection, not a control: declared and bindable,
        // but kept out of AutoUI — the deck's own next/prev is the control.
        containerProps.properties.push({ name, type: 'number', value: round(base[axis]), inherits: true, hidden: true });
    }

    const followerProps = propertiesOf(follower);
    (followerProps.transform ??= {}).position = ([0, 1, 2] as const).map(axis =>
        names.has(axis) ? `{${names.get(axis)}}` : round(base[axis]),
    ) as [TransformComponent, TransformComponent, TransformComponent];

    // One value state per page, on the container, named after the page and
    // assigned to the container itself. A second follower on the same
    // container merges into the same page state — a node holds one custom
    // state at a time, so two states per page would fight.
    const states = containerProps.states ??= {};
    const stateNames = pageStateNamesOf(container, pageNodes);
    const assignments: PageData[] = pageNodes.map(() => ({}));
    for (const [pageIndex, pose] of perPage) {
        const stateName = stateNames[pageIndex];
        const state = (states[stateName] ??= {
            values: {},
            // Legacy constraints snap: the object was simply AT the target
            // every frame. A duration here would invent an animation the
            // authored scene never had.
            transition: { duration: 0 },
        });
        for (const axis of varying) state.values[names.get(axis)!] = round(pose[axis]);
        assignments[pageIndex] = { states: [{ target: '$self', state: stateName }] };
    }
    mergePages(pages, assignments);
}

/**
 * The cycler that decides which target is active: the nearest ancestor of
 * every target that carries a `Cycler` (or an already-migrated `Pages`). All
 * targets must agree — a list spanning two cyclers isn't a page switch.
 */
function drivingContainer(
    targets: readonly LegacyConstraintNode[],
    index: SceneIndex,
): LegacyConstraintNode | undefined {
    const isCycler = (n: LegacyConstraintNode) =>
        (n.behaviors ?? []).some(b => b['@type'] === 'Cycler' || b['@type'] === 'Pages');
    let agreed: LegacyConstraintNode | undefined;
    for (const target of targets) {
        const container = index.ancestorWhere(target, isCycler);
        if (!container) return undefined;
        if (agreed && agreed !== container) return undefined;
        agreed = container;
    }
    return agreed;
}

/** Whether `node` is `ancestor` or sits beneath it. */
function contains(ancestor: LegacyConstraintNode, node: LegacyConstraintNode): boolean {
    return node.transform === ancestor.transform
        || node.transform.isDescendantOf(ancestor.transform);
}

// ============================================================================
// The solver — measure the constraint through the driver's range
// ============================================================================

/**
 * The follower's required local position, per component: a number where the
 * constraint is static, or a `{binding}` expression where a driver moves it.
 *
 * Measured, not derived. Each driver is probed at both ends of its range and
 * at the midpoint; a component that tracks linearly compiles to
 * `{low + (high - low) * driver}`, and one that doesn't is reported rather
 * than approximated.
 */
function solveConstraint(
    target: LegacyConstraintNode,
    follower: LegacyConstraintNode,
    index: SceneIndex,
    drivers: readonly Driver[],
    frame?: LegacyConstraintNode,
): [TransformComponent, TransformComponent, TransformComponent] | undefined {
    // Measured in `frame` when given (the container a follower is about to
    // nest under), else in the follower's own parent.
    const measure = (): number[] => (frame
        ? index.positionIn(target, frame)
        : index.requiredLocalPosition(target, follower)).asArray();
    const base = measure();
    const solved: TransformComponent[] = [...base];

    for (const driver of drivers) {
        const probe = (value: number): number[] => {
            const restore = readComponent(driver.node.transform, driver);
            writeComponent(driver.node.transform, driver, value);
            const measured = measure();
            writeComponent(driver.node.transform, driver, restore);
            measure(); // restore world matrices
            return measured;
        };
        const low = probe(PROBE_LOW);
        const high = probe(PROBE_HIGH);
        const mid = probe((PROBE_LOW + PROBE_HIGH) / 2);

        for (let axis = 0; axis < 3; axis++) {
            const span = high[axis] - low[axis];
            if (Math.abs(span) < EPSILON) continue;
            if (Math.abs(mid[axis] - (low[axis] + span / 2)) > EPSILON) {
                Logger.warn(`[LegacyConstraints] "${follower.name}" tracks "${driver.property}" non-linearly on axis ${axis} — pinning it to the authored value instead`);
                continue;
            }
            if (typeof solved[axis] === 'string') {
                // Two controls moving one axis is a sum, and a sum of two
                // separately-probed slopes is not something this measurement
                // establishes. Keep the first and say so.
                Logger.warn(`[LegacyConstraints] "${follower.name}" axis ${axis} is driven by more than one control; keeping the first and ignoring "${driver.property}"`);
                continue;
            }
            warnIfUnreachable(follower, driver, index);
            solved[axis] = linearExpression(low[axis], span, driver.property);
        }
    }

    return solved as [TransformComponent, TransformComponent, TransformComponent];
}

/**
 * Canopy properties cascade downward only, so a binding resolves for a
 * consumer only if the declaring node is one of its ancestors. All known
 * legacy content is authored that way — the `props` list lives on the scene
 * root — but a scene that isn't would emit a binding that never resolves.
 * That fails safe (the component keeps its authored value) and would fail
 * silently, which is the part worth fixing.
 */
function warnIfUnreachable(
    consumer: LegacyConstraintNode,
    driver: Driver,
    index: SceneIndex,
): void {
    const reachable = index.ancestorWhere(consumer, node => node === driver.owner);
    if (!reachable) {
        Logger.warn(`[LegacyConstraints] "${consumer.name}" binds "${driver.property}", declared on "${driver.owner.name}", which is not one of its ancestors — the binding will not resolve and the authored value stands`);
    }
}

/** `{offset + span * driver}`, trimmed of the terms that are identities. */
function linearExpression(offset: number, span: number, driver: string): string {
    const scaled = Math.abs(span - 1) < EPSILON ? driver : `${driver} * ${round(span)}`;
    if (Math.abs(offset) < EPSILON) return `{${scaled}}`;
    return `{${round(offset)} + ${scaled}}`;
}

/** Trim float noise from a measured value without losing authoring precision. */
function round(value: number): number {
    return Number(value.toPrecision(9));
}

function matchesAuthored(authored: Vector3, solved: readonly number[]): boolean {
    return Math.abs(authored.x - solved[0]) < EPSILON
        && Math.abs(authored.y - solved[1]) < EPSILON
        && Math.abs(authored.z - solved[2]) < EPSILON;
}

// ============================================================================
// Behavior emission
// ============================================================================

function readComponent(transform: TransformNode, component: DrivenComponent): number {
    const vector = component.kind === 'scale' ? transform.scaling : transform.position;
    return vector.asArray()[component.axis];
}

function writeComponent(transform: TransformNode, component: DrivenComponent, value: number): void {
    const vector = component.kind === 'scale' ? transform.scaling : transform.position;
    if (component.axis === 0) vector.x = value;
    else if (component.axis === 1) vector.y = value;
    else vector.z = value;
}

/** The node's Properties behavior, created (and registered) if it has none. */
function propertiesOf(node: LegacyConstraintNode): PropertiesData {
    const behaviors = node.behaviors ?? (node.behaviors = []);
    const existing = behaviors.find(b => b['@type'] === 'Properties') as PropertiesData | undefined;
    if (existing) {
        existing.properties ??= [];
        return existing;
    }
    const created: PropertiesData = { '@type': 'Properties', properties: [] };
    behaviors.push(created);
    return created;
}

function bindTransformComponent(
    node: LegacyConstraintNode,
    component: DrivenComponent,
    binding: string,
): void {
    const properties = propertiesOf(node);
    const transform = (properties.transform ??= {});
    const source = component.kind === 'scale' ? node.transform.scaling : node.transform.position;
    const tuple = (component.kind === 'scale'
        ? (transform.scale ??= [...source.asArray()] as [number, number, number])
        : (transform.position ??= [...source.asArray()] as [number, number, number]));
    tuple[component.axis] = binding;
}

function bindTransformPosition(
    node: LegacyConstraintNode,
    position: [TransformComponent, TransformComponent, TransformComponent],
): void {
    const properties = propertiesOf(node);
    (properties.transform ??= {}).position = position;
}

/**
 * The container's `Pages`, created if it has none, retiring its `Cycler`.
 * Doing it here rather than leaving it to the Cycler → Pages migration keeps
 * the page indices this compile measured and the page children Pages will
 * read in one place; that migration skips a node that already has Pages.
 */
function pagesOf(container: LegacyConstraintNode): PagesData {
    const behaviors = container.behaviors ?? (container.behaviors = []);
    const cycler = behaviors.find(b => b['@type'] === 'Cycler');
    if (cycler) remove(behaviors, cycler);
    const existing = behaviors.find(b => b['@type'] === 'Pages') as PagesData | undefined;
    if (existing) return existing;
    const created: PagesData = { '@type': 'Pages' };
    behaviors.push(created);
    return created;
}

/** Add per-page assignments, positionally, skipping ones already present. */
function mergePages(pages: PagesData, assignments: PageData[]): void {
    const merged: PageData[] = [...(pages.pages ?? [])];
    for (let i = 0; i < assignments.length; i++) {
        const incoming = assignments[i].states;
        if (!incoming) { merged[i] ??= {}; continue; }
        const current = merged[i]?.states ?? [];
        const fresh = incoming.filter(a => !current.some(c => c.target === a.target && c.state === a.state));
        merged[i] = { ...merged[i], states: [...current, ...fresh] };
    }
    pages.pages = merged;
}

function isLegacy(behavior: BehaviorData): boolean {
    return behavior['@type'] === 'Follow'
        || behavior['@type'] === 'FollowActive'
        || (behavior['@type'] === 'Properties' && Array.isArray((behavior as LegacyPropertiesData).props));
}

function remove(behaviors: BehaviorData[], behavior: BehaviorData): void {
    const at = behaviors.indexOf(behavior);
    if (at >= 0) behaviors.splice(at, 1);
}
